import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';

/** Public page for requesting a password-reset email. Always shows a success
 *  message regardless of whether the email is registered — backend mirrors
 *  this to prevent account enumeration. */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent]   = useState(false);
  const [busy, setBusy]   = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await authAPI.forgotPassword(email.trim());
    } catch { /* swallow — backend always 200s anyway */ }
    setBusy(false);
    setSent(true);
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <Link to="/" style={brand}>
          <span style={brandMark}>🎬</span>
          <span style={brandName}>CreatorKhata</span>
        </Link>

        {!sent ? (
          <>
            <h1 style={title}>Forgot your password?</h1>
            <p style={sub}>
              Enter the email you signed up with. If we find your account, we'll send a reset link.
            </p>
            <form onSubmit={submit}>
              <input
                type="email" placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)}
                required autoFocus
                style={input}
              />
              <button type="submit" disabled={busy} style={ctaPrimary}>
                {busy ? 'Sending…' : 'Send reset link →'}
              </button>
            </form>
            <p style={footNote}>
              Remembered it? <Link to="/?auth=signin" style={inlineLink}>Sign in</Link>.
            </p>
          </>
        ) : (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📨</div>
            <h1 style={title}>Check your inbox</h1>
            <p style={sub}>
              If an account exists for <strong>{email}</strong>, a reset link is on its way.
              The link expires shortly — use it soon, and only once.
            </p>
            <p style={footNote}>
              Didn't get it? Check spam, or <button type="button" onClick={() => setSent(false)} style={inlineBtn}>try a different email</button>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', background: '#fafafa',
  fontFamily: "'Inter', sans-serif",
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '1.5rem',
};
const cardStyle = {
  background: 'white', border: '1px solid #e4e4e7',
  borderRadius: 20, padding: '2.5rem 2rem',
  maxWidth: 440, width: '100%', textAlign: 'center',
  boxShadow: '0 10px 30px rgba(10,10,12,0.06)',
};
const brand = {
  display: 'inline-flex', alignItems: 'center', gap: '0.55rem',
  textDecoration: 'none', color: '#0a0a0c', marginBottom: '1.5rem',
};
const brandMark = {
  width: 32, height: 32, borderRadius: 9,
  background: 'linear-gradient(135deg,#6366f1,#ec4899)',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: '0.85rem',
};
const brandName = {
  fontFamily: "'Syne', sans-serif", fontWeight: 800,
  fontSize: '1.05rem', letterSpacing: '-0.03em',
};
const title = {
  fontFamily: "'Syne', sans-serif",
  fontWeight: 800, fontSize: '1.55rem',
  letterSpacing: '-0.02em', color: '#0a0a0c',
  margin: '0 0 0.5rem',
};
const sub = { color: '#52525b', fontSize: '0.95rem', lineHeight: 1.55, margin: '0 0 1.5rem' };
const input = {
  width: '100%', padding: '0.85rem 1rem',
  border: '1.5px solid #e4e4e7', borderRadius: 11,
  fontSize: '0.95rem', fontFamily: "'Inter', sans-serif",
  outline: 'none', marginBottom: '0.75rem',
};
const ctaPrimary = {
  width: '100%', cursor: 'pointer', border: 'none',
  background: '#0a0a0c', color: 'white',
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700, fontSize: '0.95rem',
  padding: '0.95rem', borderRadius: 12,
};
const footNote = { marginTop: '1.25rem', fontSize: '0.85rem', color: '#71717a' };
const inlineLink = { color: '#6366f1', textDecoration: 'none', fontWeight: 600 };
const inlineBtn = {
  background: 'none', border: 'none', padding: 0,
  color: '#6366f1', textDecoration: 'underline', cursor: 'pointer',
  fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 600,
};
